<template>
  <section v-if="loading || hasData">
    <page-sub-header title="Adoption" :subtitle="isDataReady ? timeSeriesSubtitle : undefined" class="mt-4">
      <template #header>
        <div class="d-flex flex-row align-items-center justify-content-end">
          <app-input
            v-model="timeSeries"
            :options="TIME_SERIES_VALUES"
            type="radio"
            buttons
            button-variant="outline-primary"
            class="text-uppercase mb-0"
            size="sm"
          />
          <export-button
            v-if="isDataReady"
            :file-name="`adoption-metrics-${timeSeries}`"
            :export-data="exportData"
            :export-file-rows="exportFileRows"
            :export-headers="exportHeaders"
            class="btn mt-0"
          />
        </div>
      </template>
    </page-sub-header>
    <sme-card v-if="loading">
      <app-loading />
    </sme-card>
    <template v-else-if="timeGroupedCountDataset.values.length">
      <chart-with-companion-kpis
        card-class="mb-4"
        chart-title="Enrolled employees over time"
        chart-subtitle="Number of employees"
      >
        <line-chart
          :labels="timeGroupDataset"
          :datasets="timeGroupedCountDataset"
          :chart-options="{
            aspectRatio: getInitialAspectRatio(3, 2),
            scales: { y: { ticks: { precision: 0 }, title: { text: timeGroupedCountDataset.name } } },
          }"
          fill-gradient
        />
        <template #companion>
          <app-stat
            :stat="lastCountValue"
            :previous-stat="prevCountValue"
            :formatter="toInteger"
            description="Enrolled Employees"
            class="mb-3"
            background-color="core-1-lighten-90"
            color="default"
            pdf-kpi
          />
          <p class="mb-0">
            {{ timeGroupTitle }}, there {{ pluralize('was', lastCountValue, 'were') }}
            {{ toInteger(lastCountValue) }} enrolled {{ pluralize('employee', lastCountValue) }}.
            {{ countPrevDiffText }}
          </p>
        </template>
      </chart-with-companion-kpis>

      <chart-with-companion-kpis chart-title="Adoption rate over time" chart-subtitle="Percentage (%)">
        <line-chart
          :labels="timeGroupDataset"
          :datasets="timeGroupedPercentageDataset"
          :chart-options="{
            aspectRatio: getInitialAspectRatio(3, 2),
            scales: { y: { title: { text: timeGroupedPercentageDataset.name } } },
          }"
          fill-gradient
        />
        <template #companion>
          <app-stat
            :stat="lastPercentageValue"
            :previous-stat="prevPercentageValue"
            :formatter="toNumber"
            suffix="%"
            description="Adoption Percentage"
            class="mb-3"
            background-color="core-1-lighten-90"
            color="default"
            pdf-kpi
          />
          <p class="mb-0">
            {{ timeGroupTitle }}, your adoption rate was {{ lastPercentageValue }}%.
            {{ percentagePrevDiffText }}
          </p>
        </template>
      </chart-with-companion-kpis>
    </template>
  </section>
</template>

<script setup>
import { computed, onBeforeMount, ref, watch } from 'vue';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppStat from '@/components/AppStat.vue';
import ChartWithCompanionKpis from '@/components/ChartWithCompanionKpis.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import LineChart from '@/components/charts/LineChart.vue';
import useAdoptionMetricsCache from '@/composables/useAdoptionMetricsCache';
import useTimeGroupDataset from '@/composables/useTimeGroupDataset';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import { TIME_SERIES_VALUES, getInitialAspectRatio } from '@/utils/chart';
import { pluralize, toInteger, toNumber } from '@/utils/common';
import ExportButton from '@/components/ExportButton.vue';

const { divisionId } = useDivisionSelection();
const { getAdoptionMetrics: fetchAdoptionMetrics } = useAdoptionMetricsCache();

const loading = ref(true);
const timeDataset = ref(null);
const countDataset = ref(null);
const percentageDataset = ref(null);

const {
  timeSeries,
  timeSeriesSubtitle,
  timeGroupDataset,
  timeGroupTitle,
  datasetToTimeGroupedDataset,
  getPrevDiffText,
} = useTimeGroupDataset(timeDataset);

const timeGroupedCountDataset = computed(() => datasetToTimeGroupedDataset(countDataset));
const timeGroupedPercentageDataset = computed(() => datasetToTimeGroupedDataset(percentageDataset));
const lastCountValue = computed(() => timeGroupedCountDataset.value?.calculated.last);
const prevCountValue = computed(() => timeGroupedCountDataset.value?.calculated.prev);
const lastPercentageValue = computed(() => timeGroupedPercentageDataset.value?.calculated.last);
const prevPercentageValue = computed(() => timeGroupedPercentageDataset.value?.calculated.prev);
const countPrevDiffText = computed(() => getPrevDiffText(timeGroupedCountDataset));
const percentagePrevDiffText = computed(() =>
  getPrevDiffText(timeGroupedPercentageDataset, value => `${Number(toNumber(value))}%`),
);

const isDataReady = computed(() => {
  return (
    timeGroupDataset.value?.values && countDataset.value?.values && percentageDataset.value?.values && !loading.value
  );
});

watch([divisionId, timeSeries], () => getAdoptionMetrics());

const getAdoptionMetrics = async () => {
  loading.value = true;

  try {
    const { results } = await fetchAdoptionMetrics(timeSeries.value, divisionId.value);

    if (results?.length) {
      const time = results.find(result => result.id === 'time');
      const adoption = results.find(result => result.id === 'adopted');
      const eligible = results.find(result => result.id === 'eligible');

      timeDataset.value = time;
      countDataset.value = {
        ...adoption,
        name: 'Number of employees',
      };
      percentageDataset.value = {
        name: 'Percentage (%)',
        values: eligible.values.map((value, index) =>
          value ? Number(toNumber((adoption.values[index] / value) * 100)) : 0,
        ),
      };
    }
  } finally {
    loading.value = false;
  }
};

const exportData = computed(() => {
  if (!isDataReady.value) {
    return [];
  }

  return timeGroupDataset.value.values.map((time, index) => ({
    time,
    employees: countDataset.value.values[index],
    percentage: percentageDataset.value.values[index],
  }));
});

const exportHeaders = computed(() => {
  if (!isDataReady.value) {
    return [];
  }

  return ['Date', countDataset.value.name, percentageDataset.value.name];
});

const exportFileRows = item => [item.time, item.employees, item.percentage];

onBeforeMount(() => getAdoptionMetrics());

const hasData = computed(() => !loading.value && !!timeGroupedCountDataset.value?.values?.length);
</script>
